import {
  Component,
  computed,
  EventEmitter,
  input,
  Input,
  output,
  Output,
  ViewChild,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { BaseListing } from '../../../pages/_base-listing/base-listing'
import { CoreService, NotificationService } from '../../../services'
import { Carrier, Order, Payment, Tag } from 'depoto-core/src/entities'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { isEqual as areDeeplyEqual } from 'lodash-es'

const optionKeepCurrent = 'keep.current'
const optionKeepCurrentSetSelected = 'keep.current.set.selected'

interface Schema {
  id: null
  reservationNumber?: null
  tags?: {
    id: null
  }
  paymentItems?: {
    id: null
    isPaid: null
    amount: null
    dateCreated: null
    payment: {
      id: null
      name: null
      type: {
        id: null
        name: null
      }
    }
  }
}
const defaultOrderChanges = {
  dateExpedition: null,
  priority: optionKeepCurrent,
  carrier: { id: optionKeepCurrent, name: optionKeepCurrent },
  tags: [],
}

@Component({
  selector: 'modal-bulk-edits-orders',
  templateUrl: './modal-bulk-edits-orders.component.html',
  styleUrl: './modal-bulk-edits-orders.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalBulkEditsOrdersComponent extends BaseListing {
  @ViewChild('modal') childModal: ModalContainerComponent
  @Input() allOrdersSelected = false
  areAllOrdersChecked = input<any>(false)
  checkedOrders = input<Order[]>([])
  setCheckedOrdersToFalse = output<void>()

  checkedMap = input<WritableSignal<Record<string, boolean>>>()

  @Input() dataCyAttribute: string
  @Input() filters: any = {}
  @Input() tags: Tag[] = []
  @Input() isShowingTags = false

  @Output() refreshOrdersPage: EventEmitter<any> = new EventEmitter<any>()
  @Output() ordersChanged: EventEmitter<any> = new EventEmitter<any>()
  selectOptions = ['keep.current.set.selected', 'keep.current.remove.selected', 'remove.current.add.selected']
  booleanSelectOptions = [optionKeepCurrent, 'yes', 'no']
  tagSelectedOption = optionKeepCurrentSetSelected
  payments = input<Payment[]>([])
  paymentOptions = computed(() =>
    this.payments()?.length
      ? [{ id: null, name: optionKeepCurrent }, ...this.payments()]
      : [{ id: null, name: optionKeepCurrent }]
  )

  paymentSelectedOption = optionKeepCurrent
  isPaidOptions = this.booleanSelectOptions
  isPaidSelectedOption = optionKeepCurrent
  deleteOrders = false

  carriers = input<Carrier[]>([])
  carrierOptions = computed(() =>
    this.carriers()?.length
      ? [{ id: null, name: optionKeepCurrent }, ...this.carriers()]
      : [{ id: null, name: optionKeepCurrent }]
  )
  priorityOptions: Array<{ id: number; name: string } | string> = [
    optionKeepCurrent,
    { id: 0, name: 'priority.lowest' },
    { id: 1, name: 'priority.low' },
    { id: 10, name: 'priority.normal' },
    { id: 20, name: 'priority.high' },
    { id: 30, name: 'priority.highest' },
  ]

  orderChanges: Partial<Order> | any = {
    dateExpedition: optionKeepCurrent,
    priority: optionKeepCurrent,
    carrier: { id: optionKeepCurrent, name: optionKeepCurrent },
    tags: [],
    payment: '',
    paymentItems: {
      id: '',
      payment: {
        id: null,
        name: null,
        type: {
          id: null,
          name: null,
        },
      },
      amount: null,
      isPaid: null,
    },
  }

  ordersFetched: Partial<Order>[] | any = []

  loading = false

  numberOfAllOrders: number
  numberOfUpdatedOrders: number
  isShowingProgress = false
  isShowingCloseBtn = false
  updateErrors = []
  wasKilled = false
  updating = false
  ordersWithErrors = []
  isProgressFinish = false
  ordersWithoutPaymentItems = []
  ordersWithMorePaymentItems = []
  deleteErrors = []

  constructor(
    protected core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {
    super(core)
  }

  async showChildModal() {
    this.loading = true
    if (!this.allOrdersSelected && this.checkedOrders()?.length < 1) {
      alert(this.translateService.instant('modal-bulk-edits.select-order'))
      return
    }
    //   await this.getOptions()
    this.setDefaultValues(true)
    this.loading = false
    this.childModal.showChildModal()
  }

  hideChildModal() {
    this.childModal.hideChildModal()
  }

  setDefaultOrderChanges() {
    this.orderChanges = JSON.parse(JSON.stringify(defaultOrderChanges))
  }

  close() {
    if (!this.isProgressFinish) {
      if (confirm(this.translateService.instant('modal-bulk-edits.terminate-action'))) {
        this.wasKilled = true
      } else return
    }
    this.setDefaultValues()
    this.hideChildModal()
  }

  setDefaultValues(isShowingModal = false): void {
    this.setDefaultOrderChanges()
    this.tagSelectedOption = optionKeepCurrentSetSelected
    this.paymentSelectedOption = optionKeepCurrent
    this.isPaidSelectedOption = optionKeepCurrent
    this.ordersFetched = []
    this.numberOfAllOrders = undefined
    this.numberOfUpdatedOrders = undefined
    this.updateErrors = []
    this.ordersWithErrors = []
    this.ordersWithoutPaymentItems = []
    this.ordersWithMorePaymentItems = []
    this.deleteErrors = []
    this.deleteOrders = false

    this.isShowingProgress = false
    this.isShowingCloseBtn = false
    this.updating = false
    this.isProgressFinish = false
    this.wasKilled = false
    if (!isShowingModal) {
      this.refreshOrdersPage.emit()
      this.setCheckedOrdersToFalse.emit()
    }
  }

  getTagValue(tag): boolean {
    return this.orderChanges.tags.some(t => t.id === tag.id)
  }

  updateTags(tag: Tag, value: boolean) {
    if (value) {
      this.orderChanges.tags.push(tag)
    } else {
      this.orderChanges.tags.forEach((t: Tag, i: number) => {
        if (t.id === tag.id) {
          this.orderChanges.tags.splice(i, 1)
        }
      })
    }
  }

  async updateOrder(order, changedProperties, schemaForUpdate) {
    if (this.wasKilled) {
      this.setDefaultValues()
      // this.hideChildModal()
      return
    }
    // todo test
    const newOrder = { id: order.id, ...changedProperties }

    // prepare paymentItems for update
    if (this.paymentSelectedOption !== optionKeepCurrent || this.isPaidSelectedOption !== optionKeepCurrent) {
      if (!order.paymentItems?.length || order.paymentItems?.length < 1) {
        this.ordersWithoutPaymentItems.push(order)
      } else {
        newOrder.paymentItems = this.preparePaymentItems(order, changedProperties)
      }
    }
    // prepare tags for update
    if (this.orderChanges.tags?.length || this.tagSelectedOption === 'remove.current.add.selected') {
      newOrder.tags = await this.setTagsOnOrderToUpdate(order)
    }

    try {
      await this.update(newOrder, schemaForUpdate)
    } catch (err) {
      let errMessage
      if (Object.prototype.toString.call(err[0]) === '[object Array]') {
        for (const object of err) {
          for (const key in object) {
            if (object.hasOwnProperty(key)) {
              errMessage = object[key]
            }
          }
        }
      } else errMessage = err[0]

      this.updateErrors.push({
        orderId: order.id,
        orderReservationNumber: order?.reservationNumber,
        errMessage: errMessage,
      })
    } finally {
      this.numberOfUpdatedOrders = this.numberOfUpdatedOrders + 1
    }
  }

  async save() {
    // prepare props for new data
    this.loading = true
    this.numberOfUpdatedOrders = 0
    this.ordersWithMorePaymentItems = []
    this.ordersWithoutPaymentItems = []
    this.deleteErrors = []
    let page = 1
    let endPage: number

    //for avoid error alerts while update
    this.notificationService.avoidErrorAlerts = true

    // Delete reservations if not all orders are selected
    if (!this.allOrdersSelected && this.deleteOrders) {
      if (this.deleteOrders && !this.checkedOrders()?.some(o => o.status?.id === 'reservation')) {
        alert(this.translateService.instant('bulk-edit-orders.delete-is-true-but-no-selected-reservations'))
        // this.loading = false //  todo loading false ?
      } else if (this.deleteOrders) {
        this.startUpdate()

        const ordersToDelete = this.checkedOrders().filter(o => o.status?.id === 'reservation')
        //todo this.checkedOrders() = this.checkedOrders().filter(o => o.status?.id !== 'reservation')
        for (const orderToDelete of ordersToDelete) {
          const mapSignal = this.checkedMap()
          if (!mapSignal) return // ignore in pages where checked isn't used
          mapSignal.update(map => ({
            ...map,
            [orderToDelete.id]: false,
          }))
        }

        for (const orderToDelete of ordersToDelete) {
          if (this.wasKilled) {
            this.setDefaultValues()
            // this.hideChildModal()
            return
          }
          try {
            await this.core.services.order.deleteReservation(orderToDelete)
          } catch (err) {
            this.deleteErrors.push({ order: orderToDelete.reservationNumber, errorMessage: err?.errorMessage }) // todo add to html
          } finally {
            this.numberOfUpdatedOrders = this.numberOfUpdatedOrders + 1
          }
        }
      }
    }

    // end here if it was just reservations to delete
    if (!this.allOrdersSelected && !this.checkedOrders()?.length) {
      this.endUpdate()
      return
    }

    // prepare schemas for fetch and update
    const schema: Schema = this.setSchema()
    const schemaForUpdate: Schema = { id: null }

    //object with changed properties from productChanges
    const changedProperties = this.setChangedProperties()

    // pokud vyberu vse, muzou to byt jen rezervace ????
    // ZJISTIM PODLE
    //   jestli neni vyfiltrovany status rezervace
    //   check jestli je nejaka bill ALE to bych musela nejdrive vsechny natahnout -> blbost?
    // je zbytecne tohle checkovat ? todo zatim preskocim tento check

    if (this.allOrdersSelected) {
      this.startUpdate()

      // loop through pages
      do {
        if (this.wasKilled) {
          this.setDefaultValues()
          this.hideChildModal()
          return
        }
        //prepare orders to update
        const { items, paginator } = await this.core.services.order.getList(
          { page: page, sort: 'id', direction: 'asc', filters: this.filters },
          schema
        )
        let itemsToUpdate = items
        // delete reservations
        if (this.deleteOrders) {
          const ordersToDelete = items.filter(o => o.status?.id === 'reservation')
          itemsToUpdate = items.filter(o => o.status?.id !== 'reservation')
          for (const orderToDelete of ordersToDelete) {
            if (this.wasKilled) {
              this.setDefaultValues()
              // this.hideChildModal()
              // todo check
              return
            }
            try {
              await this.core.services.order.deleteReservation(orderToDelete)
            } catch (err) {
              this.deleteErrors.push({ order: orderToDelete.reservationNumber, errorMessage: err?.errorMessage }) // todo add to html
            } finally {
              this.numberOfUpdatedOrders = this.numberOfUpdatedOrders + 1
            }
          }
        }
        if (!itemsToUpdate?.length) {
          page = page + 1
          // todo continue ?
          continue
        }

        if (page === 1) {
          this.numberOfAllOrders = paginator.totalCount
          this.loading = false
          this.isShowingProgress = true
          this.updating = true
          endPage = paginator.endPage
        }
        page++

        //loop and update every product
        // todo items vs itemsToUpdate check
        await Promise.all(itemsToUpdate.map(order => this.updateOrder(order, changedProperties, schemaForUpdate)))
      } while (page <= endPage)
    } else {
      if (areDeeplyEqual(this.orderChanges, defaultOrderChanges) && !changedProperties) {
        alert(this.translateService.instant('bulk-edits-orders.nothing-was-changed'))
        this.loading = false
        return
      }
      // todo natahnout payment  POUZE items pokud je potreba plus tagy
      // todo zjistit jestli byly staženy tagy při listingu - jestli jsou b columns? -> když ne, tak tady dotáhnout
      if (
        (this.tagSelectedOption !== 'remove.current.add.selected' && !this.isShowingTags) ||
        this.paymentSelectedOption !== optionKeepCurrent ||
        this.isPaidSelectedOption !== optionKeepCurrent
      ) {
        // todo is this rigt check ?
        for (const { index } of this.checkedOrders().map((value, index) => ({ index, value }))) {
          try {
            this.checkedOrders()[index] = await this.core.services.order.getById(this.checkedOrders()[index].id, schema)
          } catch (err) {
            console.error(err)
          }
        }
      }

      this.startUpdate()
      await Promise.all(this.checkedOrders().map(order => this.updateOrder(order, changedProperties, schemaForUpdate)))
    }

    this.endUpdate()
  }

  setSchema() {
    const schema: Schema = { id: null, reservationNumber: null }
    if (this.orderChanges.tags?.length && this.tagSelectedOption !== 'remove.current.add.selected')
      schema.tags = { id: null }
    if (this.paymentSelectedOption !== optionKeepCurrent || this.isPaidSelectedOption !== optionKeepCurrent) {
      schema.paymentItems = {
        id: null,
        isPaid: null,
        amount: null,
        dateCreated: null,
        payment: {
          id: null,
          name: null,
          type: {
            id: null,
            name: null,
          },
        },
      }
    }
    return schema
  }

  setChangedProperties() {
    const changedProperties = {}
    const entries = Object.entries(this.orderChanges)
    for (const [key, value] of entries) {
      if (value !== 'keep.current' && value !== null && value !== '' && key !== 'tags' && key !== 'carrier') {
        this.orderChanges[key] = value
        changedProperties[key] = value
      } else if (key === 'carrier' && Object(value)?.id !== 'keep.current') {
        const a = this.carriers().find(c => c.id === Object(value).id)
        this.orderChanges[key] = a
        changedProperties[key] = a
      } else if (
        key === 'tags' &&
        (this.orderChanges.tags?.length || Object(value)?.id !== 'remove.current.add.selected')
      ) {
        this.orderChanges[key] = this.orderChanges.tags
        changedProperties[key] = this.orderChanges.tags
      }
    }
    return changedProperties
  }

  startUpdate() {
    this.numberOfAllOrders = this.checkedOrders()?.length
    this.loading = false
    this.isShowingProgress = true
    this.ordersWithMorePaymentItems = []
    this.updating = true // todo add this to orders on top
  }

  endUpdate() {
    this.isProgressFinish = true
    this.refreshOrdersPage.emit()
    this.setCheckedOrdersToFalse.emit()
    //allow err alerts
    this.notificationService.avoidErrorAlerts = false

    // hide suspend button, show close btn
    this.updating = false
    this.isShowingCloseBtn = true
  }

  async update(order, schema) {
    try {
      order = await this.core.services.order.updateOrderPart(order, schema)
    } catch (err) {
      this.ordersWithErrors.push(order)
      this.updateErrors.push({
        orderId: order.id,
        orderReservationNumber: order?.reservationNumber,
        errMessage: err.message,
      })
    }
  }

  setTagsOnOrderToUpdate(order) {
    let tags = [...(order.tags ?? [])]
    if (this.tagSelectedOption === 'keep.current.set.selected') {
      this.orderChanges.tags.forEach(tag => {
        if (!tags.find(t => t.id === tag.id)) {
          tags.push(tag)
        }
      })
    } else if (this.tagSelectedOption === 'keep.current.remove.selected') {
      for (const changedTag of this.orderChanges.tags) {
        tags = tags.filter(tag => tag.id !== changedTag.id)
      }
    } else if (this.tagSelectedOption === 'remove.current.add.selected') {
      order.tags = this.orderChanges.tags
      tags = this.orderChanges.tags
    }
    return tags
    // TODO do not send if origin data are same
    // TODO check jestli nejsou arays puvodni a na upravu stejne => neposilat tags
  }

  preparePaymentItems(order, changedProperties) {
    changedProperties['paymentItems'] = order.paymentItems
    for (const paymentItem of changedProperties.paymentItems) {
      if (this.isPaidSelectedOption !== optionKeepCurrent) {
        paymentItem.isPaid = this.isPaidSelectedOption === 'yes'
      }
      if (this.paymentSelectedOption !== optionKeepCurrent) {
        paymentItem.payment = this.paymentSelectedOption
      }
    }
    return changedProperties.paymentItems
  }

  suspend() {
    this.wasKilled = true
  }

  filter(pageNo: number, sortProp: string, sortDir: string, concat: boolean): void {}
}
